import { LitElement, html } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { uid } from 'uid';
import '@fil-global/gds-streamline-icons/webcomponent/fil-icon-close';
import '@fil-global/gds-streamline-icons/webcomponent/fil-icon-custom-alert-circle';
import '@fil-global/gds-streamline-icons/webcomponent/fil-icon-custom-alert-diamond';
import '@fil-global/gds-streamline-icons/webcomponent/fil-icon-custom-check-circle-1';
import '@fil-global/gds-streamline-icons/webcomponent/fil-icon-custom-info-circle';

import { dispatchEvent } from '@fil-global/shared';
import '@fil-global/fil-loader';

import componentStyles from './fil-toast.styles.js';

/**
 * @tag fil-toast
 * @property {number}[duration] Duration of showing the component
 * @property {boolean}[noCloseButton] Show/hide close button
 * @property {boolean}[open] Activate the component
 * @property {'warning'| 'success'| 'info'| 'error'|'loading'}[status] Status of showing the component
 * @property {'standard'|'coloured'}[variation] Variation of the component
 * @fires close.gds.toast (event: Event) => void
 */
export class FilToast extends LitElement {
  static properties = {
    /*
     * it represents the number of milliseconds from when the Toast was
     * placed on the page before it will automatically dismiss itself.
     * A11y concerns: at least 5000ms is essential, so any duration
     * of less than 5000ms will be raised to that baseline.
     */
    duration: { type: Number },
    noCloseButton: { type: Boolean },
    open: { type: Boolean },
    status: { type: String }, // 'warning', 'success', 'info', 'error', 'loading'
    variation: { type: String }, // 'standard' | 'coloured'
  };

  set duration(duration) {
    const hasDuration = duration !== null && duration > 0;
    const newDuration = hasDuration ? Math.max(5000, duration) : null;
    const oldValue = this.duration;
    if (newDuration && this.countdownStart) {
      this.countdownStart = performance.now();
    }
    this._duration = newDuration;
    this.requestUpdate('duration', oldValue);
  }

  get duration() {
    return this._duration;
  }

  static get styles() {
    return [componentStyles];
  }

  constructor() {
    super();
    this.open = false;
    this.status = 'info';
    this.variation = 'standard';
    this.noCloseButton = false;
    this._duration = null;

    this.countdownStart = 0;
    this.nextCount = -1;
    this.uid = uid();

    this.doCountdown = time => {
      if (!this.countdownStart) {
        this.countdownStart = performance.now();
      }
      if (time - this.countdownStart > this._duration) {
        this.onClose();
        this.countdownStart = 0;
      } else {
        this.countdown();
      }
    };
    this.countdown = () => {
      cancelAnimationFrame(this.nextCount);
      this.nextCount = requestAnimationFrame(this.doCountdown);
    };
    this.holdCountdown = () => {
      this.stopCountdown();
      this.addEventListener('focusout', this.resumeCountdown);
    };
    this.resumeCountdown = () => {
      this.removeEventListener('focusout', this.holdCountdown);
      this.countdown();
    };
  }

  renderIcon() {
    const { status, variation } = this;
    const variationDefault = variation === 'standard';

    if (status === 'loading') {
      return html`<div class="fil-toast__icon">
        <fil-loader size="xs" color=${!variationDefault ? 'white' : ''}></fil-loader>
      </div>`;
    }

    switch (status) {
      case 'error':
        return html`<fil-icon-custom-alert-circle
          class="fil-toast__icon"
          color=${ifDefined(variationDefault ? 'brand-error-red' : undefined)}
          ?filled=${variationDefault}
          label="Error"
          size="24"
        ></fil-icon-custom-alert-circle>`;
      case 'success':
        return html`<fil-icon-custom-check-circle-1
          class="fil-toast__icon"
          color=${ifDefined(variationDefault ? 'green-600' : undefined)}
          ?filled=${variationDefault}
          label="Success"
          size="24"
        ></fil-icon-custom-check-circle-1>`;
      case 'warning':
        return html`<fil-icon-custom-alert-diamond
          class="fil-toast__icon"
          color=${ifDefined(variationDefault ? 'orange-600' : undefined)}
          ?filled=${variationDefault}
          label="Warning"
          size="24"
        ></fil-icon-custom-alert-diamond>`;
      default:
        return html`<fil-icon-custom-info-circle
          class="fil-toast__icon"
          color=${ifDefined(variationDefault ? 'blue-500' : null)}
          ?filled=${variationDefault}
          label="Information"
          size="24"
        ></fil-icon-custom-info-circle>`;
    }
  }

  renderCloseButton() {
    const { noCloseButton } = this;
    if (!noCloseButton) {
      return html`<div class="fil-toast__button">
        <fil-icon-close
          label="Close"
          size="16"
          tabindex="0"
          @click=${this.onClose}
        ></fil-icon-close>
      </div>`;
    }
    return null;
  }

  startCountdown() {
    this.countdown();
    this.addEventListener('focusin', this.holdCountdown);
  }

  stopCountdown() {
    cancelAnimationFrame(this.nextCount);
    this.countdownStart = 0;
  }

  onClose() {
    this.close();
    dispatchEvent(this, 'close.gds.toast', {
      value: this,
    });
  }

  close() {
    this.open = false;
  }

  onSlotChange() {
    const links = [...this.querySelectorAll('fil-link')];

    links.forEach(link => {
      if (this.variation === 'coloured') {
        if (this.status === 'warning') {
          link.setAttribute('color', 'black');
        } else if (this.status === 'error') {
          link.setAttribute('color', 'coloured-red');
        } else {
          link.setAttribute('color', 'white');
        }
      }
    });
  }

  render() {
    const classes = {
      'fil-toast--open': this.open,
      [`fil-toast--${this.variation}`]: this.variation !== 'standard',
      [`fil-toast--${this.status}`]: this.status,
    };

    return html`<div id="toast-${this.uid}" class="fil-toast${classMap(classes)}" role="alert">
      ${this.renderIcon()}
      <div class="fil-toast__body">
        <div class="fil-toast__content">
          <slot></slot>
        </div>
        <slot name="action" @slotchange=${this.onSlotChange}></slot>
      </div>
      ${this.renderCloseButton()}
    </div>`;
  }

  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has('open')) {
      if (this.open) {
        if (this.duration) {
          this.startCountdown();
        }
      } else if (this.duration) {
        this.stopCountdown();
      }
    }
    if (changedProperties.has('duration')) {
      if (this.duration !== null && this.open) {
        this.startCountdown();
      } else {
        this.stopCountdown();
      }
    }
    if (changedProperties.has('variation')) {
      if (this.variation) {
        this.onSlotChange();
      }
    }
  }
}

export default FilToast;
